/**
 * OrderCard Component
 *
 * Card component displaying order information including:
 * - Client avatar and name
 * - Order date and time range
 * - Order title
 * - Location with link
 * - Order description
 * - Respond button
 */

import { forwardRef } from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '@/src/shared/lib/utils';
import { Avatar } from '@/src/shared/ui/avatars/Avatar';
import { Typography } from '@/src/shared/ui/typography/Typography';

export interface OrderCardProps {
  /**
   * Order ID
   */
  id: string;

  /**
   * Client name
   */
  clientName: string;

  /**
   * Client profile photo URL
   */
  clientPhoto?: string;

  /**
   * Order title
   */
  title: string;

  /**
   * Order description
   */
  description: string;

  /**
   * Location/city
   */
  location: string;

  /**
   * Latitude of job location (from geocoding). null or 0 means unavailable.
   */
  latitude?: number | null;

  /**
   * Longitude of job location (from geocoding). null or 0 means unavailable.
   */
  longitude?: number | null;

  /**
   * Start date (ISO string or Date object)
   */
  startDate: string | Date;

  /**
   * End date (ISO string or Date object)
   */
  endDate: string | Date;

  /**
   * Start time (e.g., "10:00")
   */
  startTime: string;

  /**
   * End time (e.g., "14:00")
   */
  endTime: string;

  /**
   * Order budget/price (optional)
   */
  budget?: number;

  /**
   * Whether current user has already submitted a proposal for this order
   */
  hasProposal?: boolean;

  /**
   * Number of proposals submitted for this order (for client view)
   */
  proposalsCount?: number;

  /**
   * Client Actor ID (for ownership check)
   */
  clientActorId?: string;

  /**
   * Whether current user is the owner of this order
   */
  isMyOrder?: boolean;

  /**
   * Click handler for respond button
   */
  onRespond?: (id: string) => void;

  /**
   * Click handler for location link
   */
  onLocationClick?: (id: string) => void;

  /**
   * Click handler for card click (to view order details)
   */
  onClick?: (id: string) => void;

  /**
   * ISO date string when the order was published (for "Published X days ago" display)
   */
  publishedAt?: string;

  /**
   * Last activity timestamp — used internally, not displayed directly
   */
  lastActivityAt?: string;

  /**
   * Callback for "Renew" button — only shown when isMyOrder is true
   */
  onRenew?: (id: string) => void;

  /**
   * Additional CSS classes
   */
  className?: string;
}

function timeAgo(date: string | Date): string {
  const ms = Date.now() - new Date(date).getTime();
  const days = Math.floor(ms / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

function daysUntilArchive(publishedAt?: string, lastActivityAt?: string): number {
  const anchor = lastActivityAt || publishedAt;
  if (!anchor) return 7;
  const elapsed = Math.floor((Date.now() - new Date(anchor).getTime()) / 86_400_000);
  return Math.max(0, 7 - elapsed);
}

export const OrderCard = forwardRef<HTMLDivElement, OrderCardProps>(
  (
    {
      id,
      clientName,
      clientPhoto,
      title,
      description,
      location,
      latitude,
      longitude,
      startDate,
      endDate,
      startTime,
      endTime,
      budget,
      hasProposal,
      proposalsCount,
      clientActorId,
      isMyOrder,
      onRespond,
      onLocationClick,
      onClick,
      publishedAt,
      lastActivityAt,
      onRenew,
      className,
    },
    ref
  ) => {
    const t = useTranslations('clientOrders');

    const handleCardClick = () => {
      if (onClick) {
        onClick(id);
      }
    };

    const handleRespond = (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent card click
      if (hasProposal && onClick) {
        // If already applied, click navigates to order details
        onClick(id);
      } else if (onRespond) {
        // Otherwise, open proposal modal
        onRespond(id);
      }
    };

    const handleLocationClick = (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent card click
      if (onLocationClick) {
        onLocationClick(id);
      } else if (onClick) {
        onClick(id);
      }
    };

    // Format dates
    const formatDate = (date: string | Date): string => {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return dateObj.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    };

    const dateRange = `${formatDate(startDate)} - ${formatDate(endDate)}, ${startTime}-${endTime}`;

    return (
      <div
        ref={ref}
        onClick={handleCardClick}
        className={cn(
          'flex flex-col w-full max-w-[608px] bg-background-primary rounded-[24px] overflow-hidden shadow-sm border border-[#E0E0E0] p-5',
          onClick && 'cursor-pointer hover:shadow-md transition-shadow',
          className
        )}
      >
        {/* Header: Avatar, Name, and Date */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <Avatar
              src={clientPhoto}
              alt={clientName}
              size="sm"
              shape="circle"
              showBorder
              variant="default"
            />
            <Typography
              variant="h3"
              weight="bold"
              color="primary"
              className="leading-tight"
            >
              {clientName}
            </Typography>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Typography
              variant="bodySm"
              color="secondary"
              className="text-right whitespace-nowrap"
            >
              {dateRange}
            </Typography>
            {publishedAt && (
              <Typography
                variant="bodySm"
                color="tertiary"
                className="text-right whitespace-nowrap"
              >
                {t('published')} {timeAgo(publishedAt)}
              </Typography>
            )}
            {hasProposal && (
              <div className="flex items-center gap-1 px-2 py-0.5 bg-[#6AB344] bg-opacity-10 rounded-full">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  className="flex-shrink-0"
                >
                  <path
                    d="M10 3L4.5 8.5L2 6"
                    stroke="#6AB344"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <Typography
                  variant="bodySm"
                  className="text-[#6AB344] font-semibold"
                >
                  {t('applied')}
                </Typography>
              </div>
            )}
            {proposalsCount !== undefined && proposalsCount > 0 && (
              <div className="flex items-center gap-1 px-2 py-0.5 bg-[#2196F3] bg-opacity-10 rounded-full">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  className="flex-shrink-0"
                >
                  <path
                    d="M2 2h6v1H2V2zm0 2h6v1H2V4zm0 2h4v1H2V6z"
                    fill="#2196F3"
                  />
                  <path
                    d="M9 1H1v10h8V1z"
                    stroke="#2196F3"
                    strokeWidth="1"
                    fill="none"
                  />
                  <path
                    d="M10 3h1v8H3v-1"
                    stroke="#2196F3"
                    strokeWidth="1"
                    fill="none"
                  />
                </svg>
                <Typography
                  variant="bodySm"
                  className="text-[#2196F3] font-semibold"
                >
                  {proposalsCount} {proposalsCount === 1 ? t('proposal') : t('proposals')}
                </Typography>
              </div>
            )}
          </div>
        </div>

        {/* Title */}
        <Typography
          variant="h3"
          weight="bold"
          color="primary"
          className="mb-1"
        >
          {title}
        </Typography>

        {/* Location with Link */}
        <div className="flex items-center justify-between mb-3">
          <Typography
            variant="body"
            color="primary"
            className="leading-tight"
          >
            {location}
          </Typography>
          {latitude != null && latitude !== 0 && longitude != null && longitude !== 0 && (
            <button
              onClick={handleLocationClick}
              className="text-[#6AB344] font-semibold text-[16px] hover:text-[#5A9B38] transition-colors focus:outline-none"
            >
              {t('seeLocation')}
            </button>
          )}
        </div>

        {/* Budget */}
        {budget !== undefined && (
          <div className="flex items-center gap-2 mb-3">
            <Typography
              variant="h3"
              weight="bold"
              color="primary"
              className="text-[#6AB344]"
            >
              ${budget}
            </Typography>
          </div>
        )}

        {/* Separator */}
        <div className="w-full h-px bg-[#E0E0E0] mb-3" />

        {/* Description */}
        <Typography
          variant="body"
          color="primary"
          className="mb-5 leading-relaxed"
        >
          {description}
        </Typography>

        {/* Respond Button - only show if not my order */}
        {!isMyOrder && (
          <button
            onClick={handleRespond}
            className={cn(
              'w-full py-3.5 px-6 rounded-[12px]',
              'font-semibold text-[18px]',
              'transition-all duration-200',
              'focus:outline-none focus:ring-2 focus:ring-offset-2',
              hasProposal
                ? 'bg-accent-primary text-white hover:bg-accent-secondary active:scale-[0.98] focus:ring-accent-primary'
                : 'bg-[#6AB344] text-white hover:bg-[#5A9B38] active:scale-[0.98] focus:ring-[#6AB344]'
            )}
          >
            {hasProposal ? t('viewMyProposal') : t('respond')}
          </button>
        )}

        {/* Renew Button - only for order owner */}
        {isMyOrder && onRenew && (() => {
          const days = daysUntilArchive(publishedAt, lastActivityAt);
          const isUrgent = days <= 1;
          return (
            <button
              onClick={(e) => { e.stopPropagation(); onRenew(id); }}
              className={cn(
                'w-full py-3.5 px-6 rounded-[12px]',
                'font-semibold text-[18px]',
                'transition-all duration-200',
                'focus:outline-none focus:ring-2 focus:ring-offset-2',
                isUrgent
                  ? 'border border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white focus:ring-[#EF4444]'
                  : 'border border-[#6AB344] text-[#6AB344] hover:bg-[#6AB344] hover:text-white focus:ring-[#6AB344]'
              )}
            >
              {t('renew')} · {days === 0 ? t('archivesToday') : days === 1 ? t('archivesTomorrow') : t('archivesInDays', { days })}
            </button>
          );
        })()}
      </div>
    );
  }
);

OrderCard.displayName = 'OrderCard';
